/**
 * A page is a product when a custom type value (`meta.typeValues`) carries a
 * numeric price greater than zero. Same rule as the Add to Cart action.
 */
export interface ProductOffer {
    productClassId: string;
    featureId: string | null;
    price: number;
    currency?: string;
}

export interface ProductTypeValue {
    productClassId: string;
    featureId: string | null;
    price: number | null;
}

/** Every custom-type entry on a page, including ones without a usable price. */
export const listProductTypeValues = (meta: { typeValues?: unknown } | null | undefined): ProductTypeValue[] => {
    const typeValues = meta?.typeValues;
    if (!typeValues || typeof typeValues !== 'object') return [];
    return Object.entries(typeValues as Record<string, unknown>).map(([productClassId, tv]) => {
        const record = tv && typeof tv === 'object' ? tv as Record<string, unknown> : {};
        const featureRaw = record['feature-id'] ?? record.featureId;
        const rawPrice = typeof record.price === 'string' ? parseFloat(record.price) : record.price;
        return {
            productClassId,
            featureId: typeof featureRaw === 'string' && featureRaw ? featureRaw : null,
            price: typeof rawPrice === 'number' && Number.isFinite(rawPrice) ? rawPrice : null,
        };
    });
};

const priceNumber = (raw: unknown): number | null => {
    const value = typeof raw === 'string' ? parseFloat(raw) : raw;
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return null;
    return value;
};

export const productOfferFromMeta = (meta: { typeValues?: unknown } | null | undefined): ProductOffer | null => {
    const typeValues = meta?.typeValues;
    if (!typeValues || typeof typeValues !== 'object') return null;

    for (const [productClassId, tv] of Object.entries(typeValues as Record<string, unknown>)) {
        if (!tv || typeof tv !== 'object') continue;
        const record = tv as Record<string, unknown>;
        const price = priceNumber(record.price);
        if (price == null) continue;
        const featureRaw = record['feature-id'] ?? record.featureId;
        const currencyRaw = record.currency;
        return {
            productClassId,
            featureId: typeof featureRaw === 'string' && featureRaw ? featureRaw : null,
            price,
            currency: typeof currencyRaw === 'string' && currencyRaw.trim() ? currencyRaw.trim() : undefined,
        };
    }
    return null;
};

export const formatProductPrice = (offer: Pick<ProductOffer, 'price' | 'currency'>): string => {
    const amount = offer.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (!offer.currency) return amount;
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: offer.currency.toUpperCase(),
        }).format(offer.price);
    } catch {
        return `${amount} ${offer.currency}`;
    }
};
